import BonusIntelCommon from '../assets/images/intel_bonus/common_intel.png';
import BonusIntelUncommon from '../assets/images/intel_bonus/uncommon_intel.png';
import BonusIntelRare from '../assets/images/intel_bonus/rare_intel.png';
import BonusIntelHeroic from '../assets/images/intel_bonus/heroic_intel.png';
import PromoIcon from '../assets/images/category_icons/ci_commander.png';

import { GIFT_REWARD_TYPES, RARITY_TO_CONFIG_KEY } from './constants';
import { formatNumber } from './helpers';

/**
 * A purchase bonus comes from the backend, as `Gifts` on an offer:
 * [{ Type, Quantity, Rarity }] while a promo runs, null otherwise. `Type` is the
 * kind of reward, `Rarity` its tier. Everything here is presentation only — the
 * artwork and the localisation keys the backend cannot send.
 */

/**
 * How each reward kind is presented: the section heading and the artwork per rarity.
 *
 * To run a promo for a new reward kind:
 *   1. add its `Type` value to GIFT_REWARD_TYPES in constants.js
 *   2. add an entry below with a heading key and one asset per rarity
 *   3. add that heading key to all five locales in mainConfig.js
 */
const REWARD_PRESENTATION = {
    [GIFT_REWARD_TYPES.commanderIntel]: {
        titleKey: 'universalIntel',
        rarityIcons: {
            common: BonusIntelCommon,
            uncommon: BonusIntelUncommon,
            rare: BonusIntelRare,
            heroic: BonusIntelHeroic,
        },
    },
};

// Shop-wide banner announcing the promo
export const PURCHASE_BONUS_BANNER_ICON = PromoIcon;
export const PURCHASE_BONUS_BANNER_TEXT_KEY = 'purchaseBonusBanner';

/**
 * Shapes an offer's Gifts into props for PurchaseBonusSection, or null when
 * the offer carries no bonus or its reward kind has no presentation defined yet.
 * The heading is taken from the first reward, so one offer is expected to hand out
 * a single kind of reward across its rarities.
 */
export const buildPurchaseBonus = (gifts, config) => {
    const rewards = gifts ?? [];
    const heading = REWARD_PRESENTATION[rewards[0]?.Type];

    if (!rewards.length || !heading) {
        return null;
    }

    return {
        label: config.purchaseBonus,
        title: config[heading.titleKey],
        items: rewards.map((reward) => {
            const rarity = RARITY_TO_CONFIG_KEY[reward.Rarity];
            const presentation = REWARD_PRESENTATION[reward.Type];

            return {
                rarity,
                img: presentation?.rarityIcons[rarity],
                label: config[rarity],
                quantity: formatNumber(reward.Quantity),
            };
        }),
    };
};

/**
 * True when at least one of the given offers carries a bonus, which is what drives
 * the shop-wide banner.
 */
export const hasAnyPurchaseBonus = (offerLists) =>
    offerLists.some((offers) =>
        (offers ?? []).some((offer) => (offer?.Gifts ?? []).length > 0)
    );
